import { useState, useCallback, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import { snapToGrid } from '../../../utils/calendar/timeCalculations';
import { generateMobileTimeSlots } from '../../../utils/calendar/mobile/mobileTimeFormatting';

/**
 * Hook for handling time selection on mobile devices
 */
export const useMobileTimeSelection = ({
                                           workingHours,
                                           selectedDate,
                                           existingEvents = [],
                                           defaultDuration = 60,
                                           snapInterval = 15,
                                           onTimeSelect,
                                           onTimeRangeSelect,
                                       }) => {
    const [selectionMode, setSelectionMode] = useState('single'); // 'single' | 'range'
    const [selectedStartTime, setSelectedStartTime] = useState(null);
    const [selectedEndTime, setSelectedEndTime] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [showTimePicker, setShowTimePicker] = useState(false);

    // Calculate available time slots for the selected date
    const availableSlots = useMemo(() => {
        if (!selectedDate || !workingHours) return [];

        const dayOfWeek = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][selectedDate.day()];
        const dayHours = workingHours.find(wh => wh.dayOfWeek === dayOfWeek);

        if (!dayHours) return [];

        const slots = generateMobileTimeSlots(
            dayHours.openingTime,
            dayHours.closingTime,
            snapInterval
        );

        // Mark slots as available or occupied
        return slots.map(slot => {
            const slotTime = selectedDate
                .hour(slot.hour)
                .minute(slot.minute)
                .second(0);

            const isOccupied = existingEvents.some(event => {
                const eventStart = dayjs(event.startTime);
                const eventEnd = dayjs(event.endTime);
                return slotTime.isSameOrAfter(eventStart) && slotTime.isBefore(eventEnd);
            });

            return {
                ...slot,
                time: slotTime,
                isOccupied,
                isSelectable: !isOccupied,
            };
        });
    }, [selectedDate, workingHours, existingEvents, snapInterval]);

    // Handle time slot selection
    const handleSlotSelect = useCallback((slotTime) => {
        const time = dayjs(slotTime);

        if (selectionMode === 'single') {
            setSelectedStartTime(time);
            setSelectedEndTime(time.add(defaultDuration, 'minute'));
            onTimeSelect?.(time);
        } else if (selectionMode === 'range') {
            if (!selectedStartTime || (selectedStartTime && selectedEndTime)) {
                // Start new selection
                setSelectedStartTime(time);
                setSelectedEndTime(null);
            } else {
                // Complete range selection
                if (time.isAfter(selectedStartTime)) {
                    setSelectedEndTime(time);
                    onTimeRangeSelect?.(selectedStartTime, time);
                } else {
                    // If selected time is before start, swap them
                    setSelectedEndTime(selectedStartTime);
                    setSelectedStartTime(time);
                    onTimeRangeSelect?.(time, selectedStartTime);
                }
            }
        }
    }, [selectionMode, selectedStartTime, selectedEndTime, defaultDuration, onTimeSelect, onTimeRangeSelect]);

    // Handle drag selection for time range
    const handleDragStart = useCallback((slotTime) => {
        setIsDragging(true);
        setSelectedStartTime(dayjs(slotTime));
        setSelectedEndTime(null);
        setSelectionMode('range');
    }, []);

    const handleDragMove = useCallback((slotTime) => {
        if (!isDragging || !selectedStartTime) return;

        const time = dayjs(slotTime);
        if (time.isAfter(selectedStartTime)) {
            setSelectedEndTime(time);
        }
    }, [isDragging, selectedStartTime]);

    const handleDragEnd = useCallback(() => {
        if (isDragging && selectedStartTime && selectedEndTime) {
            onTimeRangeSelect?.(selectedStartTime, selectedEndTime);
        }
        setIsDragging(false);
    }, [isDragging, selectedStartTime, selectedEndTime, onTimeRangeSelect]);

    // Quick time selection helpers
    const selectMorning = useCallback(() => {
        const morning = selectedDate.hour(9).minute(0);
        const morningEnd = morning.add(defaultDuration, 'minute');
        setSelectedStartTime(morning);
        setSelectedEndTime(morningEnd);
        onTimeSelect?.(morning);
    }, [selectedDate, defaultDuration, onTimeSelect]);

    const selectAfternoon = useCallback(() => {
        const afternoon = selectedDate.hour(14).minute(0);
        const afternoonEnd = afternoon.add(defaultDuration, 'minute');
        setSelectedStartTime(afternoon);
        setSelectedEndTime(afternoonEnd);
        onTimeSelect?.(afternoon);
    }, [selectedDate, defaultDuration, onTimeSelect]);

    const selectEvening = useCallback(() => {
        const evening = selectedDate.hour(18).minute(0);
        const eveningEnd = evening.add(defaultDuration, 'minute');
        setSelectedStartTime(evening);
        setSelectedEndTime(eveningEnd);
        onTimeSelect?.(evening);
    }, [selectedDate, defaultDuration, onTimeSelect]);

    // Find next available slot
    const selectNextAvailable = useCallback(() => {
        const now = dayjs();
        const startSearch = selectedDate.isSame(now, 'day') ? now : selectedDate.hour(8).minute(0);

        const nextSlot = availableSlots.find(slot =>
            slot.isSelectable && slot.time.isAfter(startSearch)
        );

        if (nextSlot) {
            handleSlotSelect(nextSlot.time);
        }
    }, [selectedDate, availableSlots, handleSlotSelect]);

    // Clear selection
    const clearSelection = useCallback(() => {
        setSelectedStartTime(null);
        setSelectedEndTime(null);
    }, []);

    // Toggle selection mode
    const toggleSelectionMode = useCallback(() => {
        setSelectionMode(prev => prev === 'single' ? 'range' : 'single');
        clearSelection();
    }, [clearSelection]);

    // Check if a time is within selection
    const isTimeInSelection = useCallback((time) => {
        if (!selectedStartTime || !selectedEndTime) return false;

        const checkTime = dayjs(time);
        return checkTime.isSameOrAfter(selectedStartTime) &&
            checkTime.isSameOrBefore(selectedEndTime);
    }, [selectedStartTime, selectedEndTime]);

    // Get selection summary
    const getSelectionSummary = useCallback(() => {
        if (!selectedStartTime) return null;

        const duration = selectedEndTime
            ? selectedEndTime.diff(selectedStartTime, 'minute')
            : defaultDuration;

        return {
            start: selectedStartTime,
            end: selectedEndTime || selectedStartTime.add(defaultDuration, 'minute'),
            duration,
            formattedRange: `${selectedStartTime.format('HH:mm')} - ${(selectedEndTime || selectedStartTime.add(defaultDuration, 'minute')).format('HH:mm')}`,
            formattedDuration: `${Math.floor(duration / 60)}h ${duration % 60}m`,
        };
    }, [selectedStartTime, selectedEndTime, defaultDuration]);

    // Time picker visibility
    const openTimePicker = useCallback(() => setShowTimePicker(true), []);
    const closeTimePicker = useCallback(() => setShowTimePicker(false), []);

    // Handle manual time input from picker
    const handleManualTimeInput = useCallback((startTime, endTime) => {
        const start = snapToGrid(dayjs(startTime), snapInterval);
        const end = snapToGrid(dayjs(endTime), snapInterval);

        setSelectedStartTime(start);
        setSelectedEndTime(end);

        if (selectionMode === 'single') {
            onTimeSelect?.(start);
        } else {
            onTimeRangeSelect?.(start, end);
        }

        closeTimePicker();
    }, [snapInterval, selectionMode, onTimeSelect, onTimeRangeSelect, closeTimePicker]);

    return {
        // State
        selectionMode,
        selectedStartTime,
        selectedEndTime,
        isDragging,
        showTimePicker,
        availableSlots,

        // Methods
        handleSlotSelect,
        handleDragStart,
        handleDragMove,
        handleDragEnd,
        selectMorning,
        selectAfternoon,
        selectEvening,
        selectNextAvailable,
        clearSelection,
        toggleSelectionMode,
        isTimeInSelection,
        getSelectionSummary,
        openTimePicker,
        closeTimePicker,
        handleManualTimeInput,

        // Setters
        setSelectionMode,
        setSelectedStartTime,
        setSelectedEndTime,
    };
};